import { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, ChevronDown, ChevronUp, ChevronRight } from 'lucide-react';
import { formatTimeToDisplay, formatDateToFull, todayjs, parseUTCDate } from '@/utils/dateUtils';
import { getEventTypeLabel, getEventTypeEmoji } from '@/utils/eventTypeUtils';
import { DateBadge } from '@/components/ux/DateBadge';
import { BOOKING_CUTOFF_HOURS } from '@shared/constants';
import dayjs from 'dayjs';
import { useT } from '@/lib/i18n';

interface EventSelectorProps {
  events: any[];
  selectedEventId: string;
  onEventSelect: (eventId: string) => void;
  initiallyCollapsed?: boolean;
}

export function EventSelector({ 
  events, 
  selectedEventId, 
  onEventSelect,
  initiallyCollapsed = false 
}: EventSelectorProps) {
  const { t } = useT();
  const [showSelector, setShowSelector] = useState(!initiallyCollapsed);
  const [showAll, setShowAll] = useState(false);
  const selectedEvent = events.find(e => e.id === selectedEventId);

  // Filter only future events
  const now = dayjs();
  const availableEvents = events.filter((event: any) => todayjs(event.date, event.time).subtract(BOOKING_CUTOFF_HOURS, 'hours').isAfter(now));
  const VISIBLE_LIMIT = 3;
  const visibleEvents = showAll ? availableEvents : availableEvents.slice(0, VISIBLE_LIMIT);
  const hiddenCount = availableEvents.length - VISIBLE_LIMIT;

  const handleEventSelect = (eventId: string) => {
    onEventSelect(eventId);
    setShowSelector(false);
  };

  return (
    <div className="mb-6">
      <button
        onClick={() => setShowSelector(!showSelector)}
        className="w-full flex items-center justify-between mb-4 group"
      >
        <div className="flex items-center">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 transition-colors ${
            selectedEventId ? 'bg-primary-500/20' : 'bg-dark-elevated dark:bg-neutral-800'
          }`}>
            <Calendar className={`w-5 h-5 transition-colors ${
              selectedEventId ? 'text-primary-500' : 'text-dark-muted dark:text-neutral-400'
            }`} />
          </div>
          <div className="text-left">
            <div className="text-xs text-dark-muted dark:text-neutral-400 mb-0.5">{t('booking.event.selectDate')}</div>
            <div className="text-sm font-semibold dark:text-neutral-100">
              {selectedEvent 
                ? `${getEventTypeEmoji(selectedEvent.type)} ${formatDateToFull(selectedEvent.date)} · ${formatTimeToDisplay(selectedEvent.time)}`
                : t('booking.event.selectDate')}
            </div>
          </div>
        </div>
        {selectedEventId && (
          showSelector ? (
            <ChevronUp className="w-5 h-5 text-dark-muted dark:text-neutral-400 group-hover:text-primary-800 dark:group-hover:text-primary-400 transition-colors" />
          ) : (
            <ChevronDown className="w-5 h-5 text-dark-muted dark:text-neutral-400 group-hover:text-primary-800 dark:group-hover:text-primary-400 transition-colors" />
          )
        )}
      </button>
      
      {(!selectedEventId || showSelector) && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
        >
          {availableEvents.length > 0 ? (
            <>
            {/* Same event-card UI as the dashboard's BookingSection */}
            <div className="grid md:grid-cols-2 gap-2">
              {visibleEvents.map((event: any, index: number) => {
                const isSelected = event.id === selectedEventId;
                const parsed = parseUTCDate(event.date);

                return (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + index * 0.1 }}
                  >
                    <button
                      onClick={() => handleEventSelect(event.id)}
                      className={`card p-2 block w-full text-left hover:border-primary-500/50 transition-all cursor-pointer ${
                        isSelected ? 'border-primary-500' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-4 min-w-0">
                          <DateBadge date={event.date} size="xl" />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 text-md font-semibold leading-tight">
                              <span>{getEventTypeEmoji(event.type)}</span>
                              <span>{getEventTypeLabel(event.type)}</span>
                            </div>
                            <div className="text-sm text-gray-700 dark:text-gray-200 mt-0.5">
                              {parsed.weekday} · {formatTimeToDisplay(event.time)}
                            </div>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 shrink-0" />
                      </div>
                    </button>
                  </motion.div>
                );
              })}
            </div>
            {!showAll && hiddenCount > 0 && (
              <button
                type="button"
                onClick={() => setShowAll(true)}
                className="mt-3 w-full flex items-center justify-center gap-1 py-2 text-sm font-medium text-primary-800 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 transition-colors"
              >
                <span>{t('booking.event.showMore', { count: hiddenCount })}</span>
                <ChevronDown className="w-4 h-4" />
              </button>
            )}
            </>
          ) : (
            <div className="p-8 text-center border-2 border-dashed border-dark-border dark:border-neutral-700 rounded-lg">
              <Calendar className="w-12 h-12 text-dark-muted dark:text-neutral-500 mx-auto mb-2" />
              <p className="text-dark-muted dark:text-neutral-400">{t('booking.event.noEvents')}</p>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}

